/** A tab opened ahead of the request that will fill it. */
interface PlayHandle {
  /** Point the tab at the minted device, or close it if minting failed. */
  settle: (deviceId: string | null) => void;
  /** True when the browser refused the tab — the caller must offer a link instead. */
  blocked: boolean;
}

/** The same thing, for a destination that is not a screen. */
interface TabHandle {
  /** Point the tab at a URL, or close it if the thing it was opened for never arrived. */
  settle: (url: string | null) => void;
  blocked: boolean;
}

/**
 * Claim a tab now, decide where it goes later — **synchronously, inside the click**.
 *
 * A popup blocker only trusts a window opened while the user gesture is still on the
 * stack, and any `await` ends that gesture. So the blank tab is claimed first and pointed
 * somewhere once the URL is known. Calling this after an await is the bug this module
 * exists to make hard to write.
 *
 * `openScreenTab` was this, hardcoded to `/view/d/:id`; it is a thin wrapper now that only
 * knows how to build the URL, and anything else that has to survive an await before it
 * knows its destination comes here rather than growing a second bare `window.open`. Two
 * ways to open a tab is how one of them quietly grows an `await` in front of it. (A
 * screen's Edit button and the editor's Play used to be the other callers; both navigate
 * their own tab now — the library's cards are what this file is for.)
 *
 * When the browser refuses anyway — some blockers refuse even the blank open — we do NOT
 * quietly navigate the current tab instead. That would take the operator off the library
 * they are working in, which is the opposite of what they clicked for.
 */
export function openBlankTab(): TabHandle {
  const tab = window.open("about:blank", "_blank");
  return {
    blocked: tab === null,
    settle: (url) => {
      if (!tab) return;
      if (url) tab.location.replace(url);
      else tab.close();
    },
  };
}

/**
 * Open the tab a screen will live in — **synchronously, inside the click**.
 *
 * `openBlankTab` above holds the gesture; this only knows how to build the URL, and the
 * encoding lives here rather than in both. `await api.devices.open(...)` is the await it
 * exists to survive: the device id is not known until the round trip comes back.
 *
 * When the window is refused anyway, we do NOT quietly navigate the current tab instead.
 * That would take the operator off the library they are working in, which is the opposite
 * of what they clicked for. The caller surfaces a link; clicking that is itself a fresh
 * gesture.
 */
export function openScreenTab(): PlayHandle {
  const tab = openBlankTab();
  return {
    blocked: tab.blocked,
    settle: (deviceId) =>
      tab.settle(deviceId ? `/view/d/${encodeURIComponent(deviceId)}` : null),
  };
}

/**
 * Open a screen whose URL is already known — also synchronously, inside the click.
 *
 * The handle above exists to hold a blank tab open ACROSS an await, because minting a
 * device is a round trip and the gesture is gone by the time the id comes back. A caller
 * that mints nothing has no await to survive: the destination is known at click time, so
 * there is no blank tab to claim and nothing to settle afterwards.
 *
 * It lives here rather than as a bare `window.open` in the caller because the gesture
 * contract is the thing worth keeping in one place. Two ways to open a screen is how one of
 * them quietly grows an `await` in front of it.
 *
 * Returns the url when the browser refused it and null when it did not, so the caller can
 * offer a link — clicking that is itself a fresh gesture. Navigating the current tab
 * instead would take the operator off the library they are working in.
 */
export function openScreenAt(url: string): string | null {
  return window.open(url, "_blank") ? null : url;
}
